import { EditActions } from '@/components';
import { useEditableDetail } from '@/hooks';
import { cn, NOOP } from '@/utils';
import React, { memo, useEffect, useRef } from 'react';
import CurrencyInput, {
	type CurrencyInputProps,
} from 'react-currency-input-field';

export interface ICurrencyProps extends CurrencyInputProps {
	label?: string;
	labelClassName?: string;
	inputClassName?: string;
	containerClassName?: string;
	require?: boolean;
	touched?: boolean;
	errorCode?: string | undefined;
	hasServerError?: boolean;
	horizontal?: boolean;
	inputPassword?: boolean;
	isValidValue?: boolean | undefined;
	isDetail?: boolean;
	unit?: string;
}
const Currency: React.FC<ICurrencyProps> = ({
	label,
	labelClassName = '',
	require,
	inputClassName = '',
	touched,
	errorCode,
	hasServerError,
	horizontal = true,
	containerClassName = '',
	isValidValue,
	isDetail,
	unit,
	...rest
}) => {
	const { isUpdate, editable, handleLabelClick, onCancel, onSave } =
		useEditableDetail({ fieldName: rest.name as string });

	const currencyRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (editable && isUpdate && isDetail) {
			currencyRef?.current?.focus();
		}
	}, [editable]);

	return (
		<div
			className={cn(
				'flex gap-2 flex-1 w-full',
				horizontal ? 'items-center' : 'flex-col',
				containerClassName
			)}
		>
			{label && (
				<label
					className={cn(
						'text-[13px] font-medium text-font2 min-w-[110px]',
						labelClassName
					)}
				>
					{label} {require && <span className="text-red6">*</span>}
				</label>
			)}
			{isDetail && !editable ? (
				<div className="flex w-full">
					<div
						className={cn(
							'box-border w-full text-[13px] h-[30px] text-font2 py-[5px] xl:px-[11px] text-left overflow-hidden xl:hover:border xl:hover:border-gray1 cursor-text flex',
							rest.disabled &&
								'cursor-not-allowed hover:border-none hover:bg-porcelain'
						)}
						onClick={!rest.disabled ? handleLabelClick : undefined}
					>
						{rest.value ? Number(rest.value)?.toLocaleString('ko-KR') : '-'}
						{unit && (
							<p className="ps-1 text-[13px] my-auto w-[50px]">{unit}</p>
						)}
					</div>
				</div>
			) : (
				<div className="flex items-center gap-2 w-full">
					<CurrencyInput
						{...rest}
						decimalsLimit={2}
						allowNegativeValue={false}
						decimalSeparator="."
						groupSeparator=","
						intlConfig={{ locale: 'ko-KR' }}
						className={cn(
							'bg-white w-full px-[3.056vw] py-[1.944vw] xl:px-[11px] xl:py-[7px] xl:text-[13px] text-[4.444vw] xl:h-[30px] h-[11.111vw] text-font2 border border-gray1 focus:outline-none focus:border-blue2',
							inputClassName,
							(isValidValue || (touched && errorCode) || hasServerError) &&
								'!border-red8 border-[1.5px]'
						)}
						onBlur={() => {
							window.scrollTo(0, 0);
							isDetail ? NOOP : rest.onBlur;
						}}
						ref={currencyRef}
					/>
					{isUpdate && isDetail && (
						<EditActions onCancel={onCancel} onSave={onSave} />
					)}
				</div>
			)}
		</div>
	);
};

export default memo(Currency);
